import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form/dist/index.ie11';
import { useMutation } from '@apollo/react-hooks';

import TextField from 'crehana-ui/TextField';
import PhoneInput, { usePhoneNumberState } from 'Shared/PhoneInput';
import PrimaryButton from 'crehana-ui/Buttons/PrimaryButton';

import { LOGIN_TRANSLATION_KEY } from 'Jsx/Utils/transformKeys';

import UPDATE_USER_MUTATION from '../graphql/updateUserData.graphql';

import LegalCheckbox, {
  LegalErrorMessage,
  rulesForRHFControllerComponent,
} from './LegalCheckbox';
import useContactData from '../hooks/useContactData';

const validation = {
  required: { value: true, message: 'Campo requerido' },
  maxLength: { value: 100, message: 'Máximo 100 caracteres' },
};

interface BasicDataFormProps {
  redirectUrl: string;
  user?: {
    firstName: string;
    lastName: string;
    phoneCode: string;
    phoneNumber: string;
  } | null;
  withInnerPadding?: boolean;
  hideLegalInfo?: boolean;
}

const BasicDataForm: React.FC<BasicDataFormProps> = ({
  redirectUrl,
  user,
  withInnerPadding,
  hideLegalInfo = false,
}) => {
  const { t } = useTranslation(LOGIN_TRANSLATION_KEY);
  const { saveContactData } = useContactData();
  const { phoneState, setPhoneState } = usePhoneNumberState();
  const [updateUser] = useMutation(UPDATE_USER_MUTATION);
  const {
    handleSubmit,
    register,
    errors,
    formState,
    getValues,
    control,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      checkboxLegal: true,
    },
  });

  const onSubmit = async () => {
    const { firstName, lastName } = getValues();

    const updateUserResponse = await updateUser({
      variables: {
        firstName,
        lastName,
        phoneNumber: phoneState.phoneNumber,
        code: phoneState.code,
      },
    });

    const MAX_ORIGIN_URL_LENGTH = 200;

    const saveContactDataResponse = await saveContactData({
      name: `${firstName} ${lastName}`,
      phoneNumber: phoneState.fullNumber,
      originUrl: window.location.href.substring(0, MAX_ORIGIN_URL_LENGTH),
    });

    if (
      updateUserResponse.data?.updateUser.success &&
      saveContactDataResponse.success
    ) {
      window.location.href = redirectUrl;
    }
  };

  return (
    <div className="flex flex-col items-center">
      <h4 className="mb-16 text-center font-h3">
        {t('AUTH_EXTRA_DATA_FORM_TITLE', {
          defaultValue: 'Indica los datos que aparecerán en tus diplomas',
        })}
      </h4>
      <div className={withInnerPadding ? 'sm:px-56 md:px-64 lg:px-96' : ''}>
        <p className="text-center font-body2">
          {t('AUTH_EXTRA_DATA_FORM_SUBTITLE', {
            defaultValue:
              'Estos se emitirán al finalizar cada curso. Asegúrate de escribir tus nombres correctamente.',
          })}
        </p>
        <div className="flex flex-col w-full mt-40">
          <div className="mb-24">
            <TextField
              wrapperClassName="bg-white"
              maxLength={100}
              name="firstName"
              defaultValue={user?.firstName || ''}
              label={t('NAMES', 'Nombres')}
              className="w-full"
              inputRef={register(validation)}
              errorText={errors.firstName?.message}
            />
          </div>
          <div className="mb-24">
            <TextField
              wrapperClassName="bg-white"
              maxLength={100}
              name="lastName"
              defaultValue={user?.lastName || ''}
              type="text"
              label={t('LAST_NAMES', 'Apellidos')}
              className="w-full"
              inputRef={register(validation)}
              errorText={errors.lastName?.message}
            />
          </div>
          <PhoneInput
            className="mb-24"
            showAll
            onChange={setPhoneState}
            initialState={
              user?.phoneNumber
                ? {
                    code: user.phoneCode,
                    phoneNumber: user.phoneNumber,
                  }
                : undefined
            }
            label="Número de celular"
          />
          {!hideLegalInfo && (
            <div className="mb-24">
              <Controller
                control={control}
                name="checkboxLegal"
                render={({ onChange, onBlur, value }) => (
                  <LegalCheckbox
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                  />
                )}
                rules={rulesForRHFControllerComponent(t)}
              />

              {errors.checkboxLegal && (
                <LegalErrorMessage message={errors.checkboxLegal.message} />
              )}
            </div>
          )}
          <PrimaryButton
            full
            size="l"
            label={t('JOIN_FOR_FREE', 'Únete gratis')}
            onClick={handleSubmit(onSubmit)}
            disabled={!formState.isValid || !phoneState.isValid}
          />
        </div>
      </div>
    </div>
  );
};

export default BasicDataForm;
